import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { EmptyState, SectionCard, StatusChip } from '../../ui';
import { LoadingBox, ViewHeader, tableContainerSx } from './OrgCommon';
import { downloadLinkPath, downloadsPath, isQuietError, startDownload, useLicApi } from './licApi';
import { formatDate, formatSize, noteLines } from './orgFormat';

const MAX_NOTES = 6;

const ReleaseNotes = ({ notes }) => {
  const lines = noteLines(notes);
  if (!lines.length) return null;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary', '& li': { mb: 0.5 } }}>
      {lines.slice(0, MAX_NOTES).map((line, index) => (
        <Typography component="li" variant="body2" key={index}>
          {line}
        </Typography>
      ))}
      {lines.length > MAX_NOTES && (
        <Typography component="li" variant="body2">
          …and {lines.length - MAX_NOTES} more
        </Typography>
      )}
    </Box>
  );
};

/** One product: latest version with its notes and a download button, then earlier versions on request. */
const ProductCard = ({ product, edition, canDownload, downloadingId, onDownload }) => {
  const [showOlder, setShowOlder] = useState(false);
  const [latest, ...older] = product.releases || [];

  return (
    <SectionCard title={`${product.name} ${edition}`} subtitle={product.description} sx={{ height: '100%', mb: 0 }}>
      {!latest ? (
        <EmptyState title="No installer yet">
          Vavetek hasn't published a {product.name} {edition} installer yet.
        </EmptyState>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <StatusChip label={`Version ${latest.version}`} tone="brand" />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Released {formatDate(latest.publishedAtUtc)}
              {latest.sizeBytes ? ` · ${formatSize(latest.sizeBytes)}` : ''}
            </Typography>
          </Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            What's new
          </Typography>
          <ReleaseNotes notes={latest.releaseNotes} />
          <Button
            variant="contained"
            startIcon={downloadingId === latest.id ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
            disabled={!canDownload || Boolean(downloadingId)}
            onClick={() => onDownload(latest)}
            sx={{ mt: 3 }}
          >
            Download {latest.version}
          </Button>
          <Typography variant="caption" component="p" sx={{ mt: 1, wordBreak: 'break-all' }}>
            {latest.fileName}
          </Typography>

          {older.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Link component="button" type="button" variant="body2" onClick={() => setShowOlder((open) => !open)}>
                {showOlder ? 'Hide previous versions' : `Previous versions (${older.length})`}
              </Link>
              <Collapse in={showOlder}>
                <Box sx={{ ...tableContainerSx, mt: 1.5, mb: 0 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Version</TableCell>
                        <TableCell>Released</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {older.map((release) => (
                        <TableRow key={release.id} hover>
                          <TableCell>{release.version}</TableCell>
                          <TableCell>{formatDate(release.publishedAtUtc)}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              disabled={!canDownload || Boolean(downloadingId)}
                              onClick={() => onDownload(release)}
                            >
                              {downloadingId === release.id ? <CircularProgress size={16} /> : 'Download'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Collapse>
            </Box>
          )}
        </>
      )}
    </SectionCard>
  );
};

/** Installers this license covers: BAMROC, HUBAROC and CRDR in the license's edition. Downloads use short-lived signed links. */
const DownloadsView = ({ org, notify }) => {
  const { licFetch } = useLicApi();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      setData(await licFetch(downloadsPath(org.id)));
    } catch (loadError) {
      if (!isQuietError(loadError)) setError(loadError.message);
    }
  }, [licFetch, org.id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const handleDownload = async (release) => {
    setDownloadingId(release.id);
    try {
      const { path } = await licFetch(downloadLinkPath(org.id, release.id), { method: 'POST' });
      startDownload(path);
      notify(`Downloading ${release.name} ${release.edition} ${release.version}…`, 'info');
    } catch (downloadError) {
      if (!isQuietError(downloadError)) notify(downloadError.message, 'error');
    } finally {
      // The browser shows its own download progress; free the button shortly after it starts.
      setTimeout(() => setDownloadingId(null), 1500);
    }
  };

  const expiry = data?.expiresAtUtc ? ` until ${formatDate(data.expiresAtUtc)}` : '';

  return (
    <Box>
      <ViewHeader title="Downloads" onRefresh={handleRefresh} refreshing={refreshing} />
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {!data && !error && <LoadingBox />}
      {data && (
        <>
          {data.licenseUsable ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              Your {data.edition} license covers BAMROC, HUBAROC and CRDR{expiry}. Install the latest {data.edition} versions on
              your team's PCs.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 3 }}>
              This license is {data.status === 'Active' ? 'expired' : data.status?.toLowerCase() || 'inactive'}, so installers
              can't be downloaded. Contact Vavetek to renew it.
            </Alert>
          )}
          <Grid container spacing={3}>
            {data.products.map((product) => (
              <Grid item xs={12} md={6} xl={4} key={product.product}>
                <ProductCard
                  product={product}
                  edition={data.edition}
                  canDownload={data.licenseUsable}
                  downloadingId={downloadingId}
                  onDownload={handleDownload}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default DownloadsView;
